import { useEffect, useRef, useCallback } from 'react';
import { useChat } from '@services/ChatProvider';

const getWelcomeMessages = (step) => {
	switch (step) {
		case 1:
			return [
				"Hey! Welcome to your Dashboard. Let's get your store set up on Unify by NearSt and sell more 👋",
				'First, let\'s start with your business details — just search for your store, add your contact details, and hit "Get started".',
				'I can talk you through it, or you can also dive in directly in the section on the right'
			];
		case 2:
			return [
				"Great progress! You've set up your business details 🎉",
				"Now let's connect your Google Business Profile so customers can find your products online.",
				"I'm here if you need any help with this step"
			];
		case 3:
			return [
				'Almost there! Just one more step to go 🚀',
				"Let's connect your POS system to sync your inventory automatically.",
				'Which POS system do you use? I can help you get connected'
			];
		default:
			return ['Hey! Welcome to your Dashboard 👋', 'Feel free to ask me anything!'];
	}
};

const getCurrentStep = (progress) => {
	if (!progress) return 1;
	if (!progress.businessDetailsCompleted) return 1;
	if (!progress.gbpCompleted) return 2;
	if (!progress.posConnectionCompleted) return 3;
	return 'complete';
};

export const useOnboardingChat = ({ onboardingProgress, connectionState, currentShop }) => {
	const { messages, sessionId, setPageContext, triggerBotMessages, clearHistory } = useChat();

	const welcomeStartedRef = useRef(false);
	const shownStepsRef = useRef(new Set());

	// Derive current step as a primitive so effects trigger reliably on step changes
	const currentStep = getCurrentStep(onboardingProgress);

	// On mount, if messages already exist (restored from localStorage), mark welcome as started
	// and record the current step so we don't re-show its messages
	useEffect(() => {
		if (messages.length > 0) {
			welcomeStartedRef.current = true;
			const step = getCurrentStep(onboardingProgress);
			if (typeof step === 'number') {
				shownStepsRef.current.add(step);
			}
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const markStepAsShown = useCallback((step) => {
		shownStepsRef.current.add(step);
	}, []);

	// Build page context with pre-formatted system prompt context
	useEffect(() => {
		const hasGBP = !!currentShop?.gmbConnection?.status;
		const hasPOS = !!currentShop?.inventorySources?.find((s) => s.kind === 'stock');
		const posProvider = connectionState?.stock?.provider?.label;

		const lines = [
			'The user is on the Unify onboarding page.',
			'',
			'ONBOARDING STEPS:',
			'  Step 1: Business Details — search for store, confirm address, enter full name and email, create a password, accept T&Cs, click "Get started".',
			'  Step 2: Connect Google Business Profile — click "Connect Google Business Profile", authorise via Google, wait for verification.',
			'  Step 3: Connect POS system — select POS from dropdown, then either authorise via OAuth, enter manual credentials (FTP/API key), or submit a partner request. Optional product data sync.',
			'',
			`CURRENT STATE:`,
			`  Current step: ${currentStep}`,
			`  Google Business Profile: ${hasGBP ? 'Connected' : 'Not yet connected'}`,
			`  POS system: ${hasPOS ? 'Connected' : 'Not yet connected'}`
		];

		if (posProvider) {
			lines.push(`  Selected POS provider: ${posProvider} (connection in progress)`);
			lines.push(`  Provide specific guidance for connecting ${posProvider}.`);
		}

		if (currentStep === 1) {
			lines.push(
				'',
				'The user needs to fill in their business details to get started. Help them find their store, complete the form, and click "Get started".'
			);
		} else if (currentStep === 2) {
			lines.push(
				'',
				'The user needs to connect their Google Business Profile. This verifies their shop and enables product listings on Google.'
			);
		} else if (currentStep === 3 && !posProvider) {
			lines.push(
				'',
				'The user needs to select and connect their POS/inventory system. Ask which system they use if they need help choosing.'
			);
		}

		setPageContext({ systemPromptContext: lines.join('\n') });
	}, [currentStep, connectionState, currentShop, setPageContext]);

	// Show welcome messages on initial load
	useEffect(() => {
		if (welcomeStartedRef.current || messages.length > 0 || !sessionId) return;

		welcomeStartedRef.current = true;

		const welcomeTexts = getWelcomeMessages(currentStep);

		if (typeof currentStep === 'number') {
			markStepAsShown(currentStep);
		}

		triggerBotMessages(welcomeTexts);
	}, [sessionId, currentStep, messages.length, triggerBotMessages, markStepAsShown]);

	// Show transition messages on step changes
	useEffect(() => {
		if (!sessionId || messages.length === 0) return;

		if (typeof currentStep !== 'number' || shownStepsRef.current.has(currentStep)) {
			return;
		}

		markStepAsShown(currentStep);

		const transitionMessages = getWelcomeMessages(currentStep);
		triggerBotMessages(transitionMessages);
	}, [currentStep, sessionId, messages.length, triggerBotMessages, markStepAsShown]);

	// Clear history when onboarding completes
	useEffect(() => {
		if (currentStep === 'complete') {
			clearHistory();
			shownStepsRef.current.clear();
			welcomeStartedRef.current = false;
		}
	}, [currentStep, clearHistory]);
};
